/**
 * Classe Chord
 *
 * @author Fabien Bellanger
 */
export default class Chord
{
    /**
     * Constructeur
     *
     * @author Fabien Bellanger
     */
    constructor()
    {
        // Variables
        // ---------
        this.root = '';
        this.alteration = '';
        this.formula = '';
        this.schema = '';
        this.bass = '';
        this.bassAlteration = '';
        this.other = '';

        /*
        Dans Guitar Pro
        ===============
        
        - Fondamentale + altération
        - Type
        - Extension
        - "Altération"
        - Basse + altération
        */
    }

    /**
     * Retourne un accord vide
     *
     * @static
     * @author Fabien Bellanger
     * @return {Object}
     */
    static getEmpty()
    {
        return {
            root:           '',
            alteration:     '',
            formula:        '',
            schema:         '',
            bass:           '',
            bassAlteration: '',
            other:          '',
        };
    }
  
    /**
     * Nom complet de l'accord sous forme de tableau
     *
     * @static
     * @author Fabien Bellanger
     * @param {Object} chord Accord
     * @return {Array}
     */
    static getName(chord)
    {
        if (chord.root === '')
        {
            return ['', ''];
        }
        else
        {
            let chordTmp = [chord.root + chord.alteration, (chord.formula !== 'M' ? chord.formula : '')];

            if (chord.bass !== undefined && chord.bassAlteration !== undefined && chord.bass !== '' &&
                (chord.bass !== chord.root || chord.bassAlteration !== chord.alteration))
            {
                chordTmp[1] += '/' + chord.bass + chord.bassAlteration;
            }

            return chordTmp;
        }
    }

    /**
     * Liste les différentes temps dans une mesure
     *
     * @static
     * @author Fabien Bellanger
     * @return {Array}
     */
    static getMeasureTimes()
    {
        return [1, 2, 3, 4];
    }

    /**
     * Liste les différentes notes
     *
     * @static
     * @author Fabien Bellanger
     * @return {Array}
     */
    static getRoots()
    {
        return ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
    }

    /**
     * Liste les différentes altérations
     *
     * @static
     * @author Fabien Bellanger
     * @return {Array}
     */
    static getAlterations()
    {
        return ['♭', '♯'];
    }

    /**
     * Liste les différents types d'accord
     *
     * @static
     * @author Fabien Bellanger
     * @return {Array}
     */
    static getFormulas()
    {
        return [
            'M', 'm', '5', 'sus2', 'sus4', '+', '7', '7sus4', 'm7', 'M7', 'm7♭5', 'mM7', '°', '9', '11', '13', '9sus4', '13sus4',
            'add9', 'madd9', '6', 'm6', 'm♭6', 'M7♯11', 'm9', 'm11', 'm13', 'mM9', 'M9', 'M13', '9♭5', '7♭9', '7♯9', '7♭5♯9', '7♯11',
            '7♭5', '+9', 'm9♭5', '+7♭9', '+7♯9',
        ];
    }

    /**
     * Liste les basses
     *
     * @static
     * @author Fabien Bellanger
     * @return {Array}
     */
    static getBasses()
    {
        return Chord.getRoots();
    }

    /**
     * Liste les basses
     *
     * @static
     * @author Fabien Bellanger
     * @return {Array}
     */
    static getBassAlterations()
    {
        return Chord.getAlterations();
    }

    /**
     * L'accord est-il vide ?
     *
     * @static
     * @author Fabien Bellanger
     * @return {Boolean}
     */
    static isEmpty(chord)
    {
        return chord.root === null || chord.root === '';
    }

    /**
     * Retourne la taille de la police pour afficher un accord
     *
     * @static
     * @author Fabien Bellanger
     * @param {String} chord            Nom de l'accord
     * @param {Number} chordNumber      Taille de la zone (1 : toute la mesure, 2 : la moitié de la mesure, 4 : un quart de la mesure)
     * @param {Number} defaultWidth     Longueur
     * @return {number}                 Taille de la police
     */
    static getChordFontSize(chord, chordNumber, defaultWidth)
    {
        const chordLength = chord.length;

        let defaultFontSize, fontSize;

        switch (chordNumber)
        {
            case 1:
                // Toute la mesure
                // ---------------
                defaultFontSize = 20;

                if (defaultWidth < 170 && defaultWidth >= 140)
                {
                    defaultFontSize -= 1;
                }
                else if (defaultWidth < 140 && defaultWidth >= 110)
                {
                    defaultFontSize -= 2;
                }
                else if (defaultWidth < 110 && defaultWidth >= 80)
                {
                    defaultFontSize -= 3;
                }
                else if (defaultWidth < 80 && defaultWidth >= 50)
                {
                    defaultFontSize -= 4;
                }
                else if (defaultWidth < 50)
                {
                    defaultFontSize -= 4;
                }

                if (chordLength <= 8)
                {
                    fontSize = defaultFontSize;
                }
                else if (chordLength > 8 && chordLength <= 10)
                {
                    fontSize = defaultFontSize - 3;
                }
                else
                {
                    fontSize = defaultFontSize - 4;
                }

                break;
            case 2:
                // La moitié de la mesure
                // ----------------------
                defaultFontSize = 18;

                if (defaultWidth < 170 && defaultWidth >= 140)
                {
                    defaultFontSize -= 1;
                }
                else if (defaultWidth < 140 && defaultWidth >= 110)
                {
                    defaultFontSize -= 2;
                }
                else if (defaultWidth < 110 && defaultWidth >= 80)
                {
                    defaultFontSize -= 3;
                }
                else if (defaultWidth < 80 && defaultWidth >= 50)
                {
                    defaultFontSize -= 4;
                }
                else if (defaultWidth < 50)
                {
                    defaultFontSize -= 4;
                }

                if (chordLength <= 4)
                {
                    fontSize = defaultFontSize;
                }
                else if (chordLength > 4 && chordLength <= 6)
                {
                    fontSize = defaultFontSize - 2;
                }
                else if (chordLength > 6 && chordLength <= 8)
                {
                    fontSize = defaultFontSize - 4;
                }
                else
                {
                    fontSize = defaultFontSize - 6;
                }
                
                break;
            default:
                // Le quart de la mesure
                // ---------------------
                defaultFontSize = 16;

                if (defaultWidth < 90)
                {
                    defaultFontSize -= 1;
                }

                if (chordLength <= 2)
                {
                    fontSize = defaultFontSize;
                }
                else if (chordLength > 2 && chordLength <= 4)
                {
                    fontSize = defaultFontSize - 1;
                }
                else if (chordLength > 4 && chordLength <= 6)
                {
                    fontSize = defaultFontSize - 3;
                }
                else if (chordLength > 6 && chordLength <= 8)
                {
                    fontSize = defaultFontSize - 5;
                }
                else
                {
                    fontSize = defaultFontSize - 6;
                }
        }
        
        return fontSize;
    }

    /**
     * Affichage d'une mesure avec 1 accord
     *
     * @static
     * @author Fabien Bellanger
     * @param {Object} draw             SVG
     * @param {Object} group            Groupe
     * @param {String} chord            Nom de l'acccord
     * @param {String} position         Position dans la mesure
     *                                  '':   Toute la mesure
     *                                  'LT': Au haut à gauche
     *                                  'BR': En bas à droite
     *                                  'L':  A gauche
     *                                  'T':  En haut
     *                                  'R':  A droite
     *                                  'B':  En bas
     * @param {Number} chordNumber      Taille de la zone (1 : toute la mesure, 2 : la moitié de la mesure, 4 : un quart de la mesure)
     * @param {Number} defaultWidth     Longueur
     * @param {Number} defaultHeight    Hauteur
     */
    static drawChord(draw, group, chord, position, chordNumber, defaultWidth, defaultHeight)
    {
        const fontSize = Chord.getChordFontSize(chord[0] + chord[1], chordNumber, defaultWidth);
        const text     = draw.text(function(add)
        {
            add.tspan(chord[0]).font({ anchor: 'middle', size: fontSize, family: 'Roboto' });
            add.tspan(chord[1]).font({ anchor: 'middle', size: fontSize - 2, family: 'Roboto' });
        });

        if (chordNumber === 1)
        {
            // 1 accord par mesure
            // -------------------
            text.move(defaultWidth / 2, defaultHeight / 2 - fontSize / 2);
        }
        else if (chordNumber === 2)
        {
            // 2 accords par mesure
            // --------------------
            if (position === 'LT')
            {
                // Zone en haut à gauche
                // ---------------------
                text.move(defaultWidth / 4, defaultHeight / 3 - fontSize);
            }
            else
            {
                // Zone en bas à droite
                // --------------------
                text.move(3 * defaultWidth / 4, 3 * defaultHeight / 4 - fontSize / 4);
            }
        }
        else
        {
            // 4 accords par mesure
            // --------------------
            if (position === 'L')
            {
                // Zone à gauche
                // -------------
                text.move(defaultWidth / 4 - fontSize / 2, defaultHeight / 2 - fontSize / 2);
            }
            else if (position === 'T')
            {
                // Zone en haut
                // ------------
                text.move(defaultWidth / 2, defaultHeight / 4 - fontSize);
            }
            else if (position === 'R')
            {
                // Zone à droite
                // -------------
                text.move(3 * defaultWidth / 4 + fontSize / 2, defaultHeight / 2 - fontSize / 2);
            }
            else
            {
                // Zone en bas
                // -----------
                text.move(defaultWidth / 2, 3 * defaultHeight / 4 - fontSize / 8);
            }
        }

        group.add(text);
    }
};
